import Typography from '@mui/material/Typography';
import {formatCopy} from 'utils/typography';
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useFormContext, Controller} from "react-hook-form";

import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import {LinkPopover} from '@vedmarket/ui-kit';

import {checkDecimal, checkDecimalsAfterDot} from "../../../../../../../../../../../../../utils/validation";
import {ADORNMENTS} from "../../../../../../../../../../../../../config/constants";
import {useLazyGetPalletesQuery} from "../../../../../../../../../../../../../api/palletes";
import {useFormattedDecimalInput} from "../../../../../../../../../../../../../utils/hooks";

import PalletesType from '../palletes_type';
import { PACKAGE_FORM, PackageForm } from "../add_form/helpers";


type Option = {
  value: string
  label: string
}

type PalletOption = {
  code: string
  name: string
}

enum STATUSES {
  LOADING = 'loading',
  SUCCESS = 'success',
  NO_DATA = 'no_data'
}

const PalletesFields = () => {

  const { t } = useTranslation()

  const { register, getValues, control, formState: { errors } } = useFormContext<PackageForm>()

  const decimalInput = useFormattedDecimalInput();

  const [ defaultPalletType, setDefaultPalletType ] = useState<Option>()

  const [ defaultPalletTypeRequestStatus, setDefaultPalletTypeRequestStatus ] = useState<STATUSES>(STATUSES.LOADING)

  const [ hasTwoTiers, setHasTwoTiers ] = useState(false);

  const errorValue = t('errors__message__incorrectValueTwoDecimalsAfterComma');

  const [ palletesRequest ] = useLazyGetPalletesQuery()

  const userPalletType = getValues()[PACKAGE_FORM.PALLETE_TYPE]

  useEffect(() => {
    if (userPalletType) {
      palletesRequest("")?.then((response) => {
        const currentPalletType = response.data.find((item: PalletOption) => item.code === userPalletType);
        setDefaultPalletType({
          value: currentPalletType.code,
          label: currentPalletType.name,
        })
        setDefaultPalletTypeRequestStatus(STATUSES.SUCCESS);
      }).catch(() => setDefaultPalletTypeRequestStatus(STATUSES.NO_DATA));
      setHasTwoTiers(Boolean(getValues()[PACKAGE_FORM.TWO_TIERS_ALLOWED]))
    } else {
      setDefaultPalletTypeRequestStatus(STATUSES.NO_DATA);
    }
  }, [ userPalletType ])


  const shouldShowPalletsType = [ STATUSES.SUCCESS, STATUSES.NO_DATA ].includes(defaultPalletTypeRequestStatus);
  return <>
    {shouldShowPalletsType && <div className='product-field product-field_pallet-type'>
      <PalletesType defaultPalletType={defaultPalletType} />
      <LinkPopover
        linkText={t('calc__help__palletTypeLink')}
        text={formatCopy(t('calc__help__palletType'))}
      />
    </div>}
    <div className='add-product__palletes-fields'>
      <div className='product-field'>
        <TextField
          {...register(PACKAGE_FORM.PALLETE_CNT, { required: true, validate: (value?: number) => value ? checkDecimal(value, t('errors__message__incorrectValue')) : t('errors__message__incorrectValue'), min: { value: 1, message: t('errors__message__incorrectValue')} })}
          error={Boolean(errors[PACKAGE_FORM.PALLETE_CNT])}
          helperText={errors[PACKAGE_FORM.PALLETE_CNT]?.message}
          variant='outlined'
          label={t('calc__text__packaging__numberOfPalletes')}
          InputProps={{
            endAdornment: ADORNMENTS.AMOUNT,
          }}
          InputLabelProps={{ shrink: true }}
        />
        <LinkPopover
          linkText={t('calc__help__palletCountLink')}
          text={formatCopy(t('calc__help__palletCount'))}
        />
      </div>
      <div className='product-field product-field_loaded-pallet'>
        <div className='product-field__pair'>
          <div className='product-field'>
            <Controller
              name={PACKAGE_FORM.PALLETE_HEIGHT}
              control={control}
              rules={{
                required: true,
                validate: (value) => value ? checkDecimalsAfterDot(value, errorValue) : errorValue,
                min: { value: 0, message: errorValue },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  value={decimalInput.getDisplayValue({
                    fieldName: PACKAGE_FORM.PALLETE_HEIGHT,
                    rawValue: field.value,
                  })}
                  onChange={(event) => {
                    decimalInput.startEditing(PACKAGE_FORM.PALLETE_HEIGHT);
                    const value = event.target.value;
                    const valueWithDot = value.replace(',', '.');
                    field.onChange(valueWithDot);
                  }}
                  onBlur={() => {
                    field.onBlur();
                    decimalInput.stopEditing(PACKAGE_FORM.PALLETE_HEIGHT);
                  }}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  variant='outlined'
                  label={t('calc__text__packaging__heightOfThePallet')}
                  InputProps={{ endAdornment: ADORNMENTS.SIZE_SMALL }}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </div>
          <div className='product-field'>
            <Controller
              name={PACKAGE_FORM.PALLETE_WEIGHT}
              control={control}
              rules={{
                required: true,
                validate: (value) => value ? checkDecimalsAfterDot(value, errorValue) : errorValue,
                min: { value: 0, message: errorValue },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  value={decimalInput.getDisplayValue({
                    fieldName: PACKAGE_FORM.PALLETE_WEIGHT,
                    rawValue: field.value,
                  })}
                  onChange={(event) => {
                    decimalInput.startEditing(PACKAGE_FORM.PALLETE_WEIGHT);
                    const value = event.target.value;
                    const valueWithDot = value.replace(',', '.');
                    field.onChange(valueWithDot);
                  }}
                  onBlur={() => {
                    field.onBlur();
                    decimalInput.stopEditing(PACKAGE_FORM.PALLETE_WEIGHT);
                  }}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  variant='outlined'
                  label={t('calc__text__packaging__weightOfThePallet')}
                  InputProps={{ endAdornment: ADORNMENTS.WEIGHT }}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </div>
        </div>
        <LinkPopover
          linkText={t('calc__help__loadedPalletLink')}
          text={formatCopy(t('calc__help__loadedPallet'))}
        />
      </div>
    </div>
    <div className='product-field product-field_toggle add-product__palletes-two_tiers'>
      <FormControlLabel
        className={`add-product__${PACKAGE_FORM.TWO_TIERS_ALLOWED}`}
        {...register(PACKAGE_FORM.TWO_TIERS_ALLOWED)}
        control={<Checkbox checked={hasTwoTiers} />}
        onChange={() => setHasTwoTiers(!hasTwoTiers)}
        label={t('calc__text__packaging__twoTiersAllowed')}
        sx={{ margin: 0 }}
      />
      <LinkPopover
        linkText={t('calc__help__stackabilityLink')}
        text={formatCopy(t('calc__help__stackability'))}
      />
    </div>
  </>

}

export default PalletesFields
